/**
 * audioSentryPort — the injection seam for the Sentry half of the episode sink.
 *
 * Why injection rather than a direct `@sentry/*` import here: the browser
 * client is initialized by `@sentry/react` 7.119.2 in apps/web, but this
 * package depends on `@sentry/core` 8.4.0 — a different major with a different
 * global carrier, so a captureMessage issued from here through v8 would not
 * reliably reach the v7 client. The whole point of the durable sink is that the
 * episode *arrives*, so apps/web registers a port backed by its own
 * (correctly-initialized) `@sentry/react` functions, exactly as it already
 * registers the repository with `setRepository` in config/sentry.ts.
 *
 * When no port is registered (tests, non-web apps) the sink simply skips the
 * Sentry write; GA still fires.
 */

export type AudioSentryLevel = 'info' | 'warning' | 'error'

export interface AudioSentryMessage {
  message: string
  level: AudioSentryLevel
  /** Queryable scalar tags. */
  tags: Record<string, string | number | boolean>
  /** Structured contexts (e.g. `audioRing`, `stats`). */
  contexts: Record<string, Record<string, unknown>>
  /** Per-fork fingerprint so forks don't collapse into one Sentry issue. */
  fingerprint: string[]
}

export interface AudioSentryBreadcrumb {
  category: string
  message?: string
  level?: AudioSentryLevel
  data?: Record<string, unknown>
}

export interface AudioSentryPort {
  captureMessage: (event: AudioSentryMessage) => void
  addBreadcrumb: (crumb: AudioSentryBreadcrumb) => void
  /** Best-effort flush before a reload (used by the reload-survival path). */
  flush: (timeoutMs: number) => Promise<boolean>
}

let port: AudioSentryPort | null = null

/** Register the app's Sentry-backed port. Call once at startup (apps/web). */
export function setAudioSentryPort(p: AudioSentryPort | null): void {
  port = p
}

/** The registered port, or null when none is wired. */
export function getAudioSentryPort(): AudioSentryPort | null {
  return port
}
